// Versus lobby: pick a mech, press FIND MATCH, wait for a challenger, then the first
// round starts (VersusScene). First to 3 round wins takes the match.

import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { COLORS, css } from '../config/palette';
import { save } from '../core/save';
import { MECH_IDS, type MechId } from '../core/types';
import { MECHS } from '../data/mechs';
import { versus, type RoundMsg } from '../net/versus';
import { gridBackdrop } from '../ui/backdrops';
import { MenuNav, Panel, TYPE, UI, createButton, footerHints, setMenuCursor, sfx, txt, type UIButton } from '../ui/kit';

const LEVELS = ['EASY', 'NORMAL', 'HARD'];

export class VersusLobbyScene extends Phaser.Scene {
  private nav!: MenuNav;
  private status!: Phaser.GameObjects.Text;
  private detail!: Phaser.GameObjects.Text;
  private findBtn!: UIButton;
  private aiBtn!: UIButton;
  private levelBtn!: UIButton;
  /** AI difficulty: 0 easy · 1 normal · 2 hard (kept between visits). */
  private level = VersusLobbyScene.lastLevel;
  private static lastLevel = 1;
  private mechName!: Phaser.GameObjects.Text;
  private mechInfo!: Phaser.GameObjects.Text;
  private portrait: Phaser.GameObjects.Image | null = null;
  private mech: MechId = 'volt';
  private offs: (() => void)[] = [];
  private dots = 0;
  private dotT = 0;
  private leaving = false;

  constructor() {
    super(SCENES.VERSUS_LOBBY);
  }

  create(): void {
    this.leaving = false;
    setMenuCursor(this, true);
    gridBackdrop(this, COLORS.threat);
    versus.leave();
    const unlocked = MECH_IDS.filter((id) => save.isUnlocked(MECHS[id].unlock));
    this.mech = unlocked.includes(save.data.lastMech) ? save.data.lastMech : 'volt';

    const pw = 1180;
    const ph = 830;
    const px = (UI.W - pw) / 2;
    const py = (UI.H - ph) / 2 - 20;
    new Panel(this, px, py, pw, ph, { fill: COLORS.uiBg, fillAlpha: 0.94, accent: COLORS.threat, ticks: true, chamfer: 22, header: 96, headerAlpha: 0.1, scanlines: true });
    txt(this, px + 52, py + 22, 'VERSUS', { font: 'display', size: TYPE.h1, weight: 700, spacing: 8 });
    txt(this, px + pw - 52, py + 40, 'ONLINE OR VS AI  ·  FIRST TO 3 ROUNDS', { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 3, originX: 1 });

    // Mech card (left).
    const cx = px + 300;
    this.mechName = txt(this, cx, py + 124, '', { font: 'display', size: TYPE.h2, weight: 700, spacing: 6, originX: 0.5 });
    this.mechInfo = txt(this, cx, py + 548, '', { size: TYPE.small, color: COLORS.uiDim, originX: 0.5, align: 'center', wrap: 420 });

    this.nav = new MenuNav(this, { layout: 'vertical', onBack: () => this.back() });
    const prev = createButton(this, cx - 150, py + 664, { label: '‹', width: 80, height: 56, align: 'center', onClick: () => this.cycle(-1, unlocked) });
    const next = createButton(this, cx + 150, py + 664, { label: '›', width: 80, height: 56, align: 'center', onClick: () => this.cycle(1, unlocked) });
    txt(this, cx, py + 664, 'MECH', { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 3, originX: 0.5, originY: 0.5 });
    void prev;
    void next;

    // Rules + matchmaking (right).
    const rx = px + 660;
    const rules = [
      'FIND MATCH pairs you with the next pilot searching online. VS AI starts an offline duel against a computer pilot.',
      'Win 3 rounds to take the match. Every round starts with your mech\'s starting kit.',
      'The arena drops surprises: weapons, repair packs and mystery items. First to touch one gets it.',
      'Blocks break, so cover does not last.',
    ];
    txt(this, rx, py + 140, 'RULES OF ENGAGEMENT', { font: 'display', size: TYPE.label, color: COLORS.threat, spacing: 3 });
    txt(this, rx, py + 180, rules.map((r) => `·  ${r}`).join('\n\n'), { size: TYPE.small, color: COLORS.uiText, wrap: 460 });

    this.findBtn = createButton(this, rx + 230, py + 612, { label: 'Find match', width: 460, height: 72, variant: 'primary', accent: COLORS.threat, hint: 'confirm', onClick: () => this.toggleSearch() }, this.nav);
    // Offline duel against an AI pilot (difficulty cycles on its own button).
    this.aiBtn = createButton(this, rx + 155, py + 694, { label: 'VS AI', width: 310, height: 64, accent: COLORS.uiGood, sublabel: 'OFFLINE', sublabelColor: COLORS.uiBad, onClick: () => this.startAI() }, this.nav);
    this.levelBtn = createButton(this, rx + 385, py + 694, { label: LEVELS[this.level], width: 150, height: 64, align: 'center', accent: COLORS.uiGood, onClick: () => this.cycleLevel() }, this.nav);
    createButton(this, rx + 230, py + 770, { label: 'Back', width: 460, height: 56, align: 'center', sound: 'ui_back', onClick: () => this.back() }, this.nav);
    this.status = txt(this, rx + 230, py + 512, 'READY', { font: 'display', size: TYPE.h3, weight: 700, spacing: 4, originX: 0.5, color: COLORS.uiDim });
    this.detail = txt(this, rx + 230, py + 550, '', { size: TYPE.small, color: COLORS.uiDim, originX: 0.5 });

    footerHints(this, [
      { action: 'navigateV', label: 'Select' },
      { action: 'confirm', label: 'Confirm' },
      { action: 'back', label: 'Back' },
    ]);
    this.nav.focus(this.findBtn, { silent: true });
    this.input.keyboard?.on('keydown-LEFT', () => this.cycle(-1, unlocked));
    this.input.keyboard?.on('keydown-RIGHT', () => this.cycle(1, unlocked));
    this.showMech();

    this.offs.push(
      versus.on('status', () => this.refresh()),
      versus.on('round', (m) => this.startRound(m as unknown as RoundMsg)),
    );
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      for (const off of this.offs) off();
      this.offs = [];
    });
    this.cameras.main.fadeIn(220, 5, 7, 11);
  }

  private cycle(dir: number, list: MechId[]): void {
    if (versus.status !== 'idle' && versus.status !== 'error') return;
    const i = list.indexOf(this.mech);
    this.mech = list[(i + dir + list.length) % list.length];
    sfx('ui_click');
    this.showMech();
  }

  private showMech(): void {
    const def = MECHS[this.mech];
    this.mechName.setText(def.name.toUpperCase()).setColor(css(def.reactorColor));
    this.mechInfo.setText(`${def.tagline}\n${def.playstyle.toUpperCase()}`);
    this.portrait?.destroy();
    const key = this.textures.exists(`mech_${this.mech}_portrait`) ? `mech_${this.mech}_portrait` : `art_portrait_${this.mech}`;
    if (this.textures.exists(key)) {
      // Fit between the name and the description (portraits are square with margins).
      const img = this.add.image(this.mechName.x, this.mechName.y + 230, key);
      img.setScale(Math.min(360 / img.height, 1));
      this.portrait = img;
    }
  }

  private cycleLevel(): void {
    this.level = (this.level + 1) % LEVELS.length;
    VersusLobbyScene.lastLevel = this.level;
    this.levelBtn.setLabel(LEVELS[this.level]);
    sfx('ui_click');
  }

  /** Offline duel: an AI pilot in a random mech, same rules as online. */
  private startAI(): void {
    const s = versus.status;
    if (s === 'matched' || s === 'playing') return;
    save.data.lastMech = this.mech;
    save.persist();
    const bot = MECH_IDS[Math.floor(Math.random() * MECH_IDS.length)];
    versus.startAI('YOU', this.mech, bot, this.level);
    this.refresh();
  }

  private toggleSearch(): void {
    const s = versus.status;
    if (s === 'connecting' || s === 'queued') {
      versus.leave();
      this.refresh();
      return;
    }
    if (s === 'matched' || s === 'playing') return;
    save.data.lastMech = this.mech;
    save.persist();
    versus.findMatch(`PILOT-${Math.floor(1000 + Math.random() * 9000)}`, this.mech);
    this.refresh();
  }

  private refresh(): void {
    const s = versus.status;
    const label: Record<string, [string, number, string]> = {
      idle: ['READY', COLORS.uiDim, ''],
      connecting: ['CONNECTING', COLORS.ultBar, 'Reaching the matchmaker…'],
      queued: ['SEARCHING', COLORS.ultBar, 'Waiting for a challenger…'],
      matched: ['CHALLENGER FOUND', COLORS.uiGood, ''],
      playing: ['CHALLENGER FOUND', COLORS.uiGood, ''],
      over: ['READY', COLORS.uiDim, ''],
      error: ['OFFLINE', COLORS.uiBad, versus.error || 'The versus server is unreachable.'],
    };
    const [t, c, d] = label[s] ?? label.idle;
    this.status.setText(t).setColor(css(c));
    if (s === 'matched' || s === 'playing') {
      const o = versus.opponent;
      this.detail.setText(o ? `${MECHS[this.mech].name.toUpperCase()}  vs  ${MECHS[o.mech]?.name.toUpperCase() ?? o.mech}  (${o.name})` : '');
    } else this.detail.setText(d);
    this.findBtn.setLabel(s === 'connecting' || s === 'queued' ? 'Cancel search' : 'Find match');
  }

  private startRound(round: RoundMsg): void {
    if (this.leaving) return;
    this.leaving = true;
    this.refresh();
    sfx('ui_confirm');
    this.time.delayedCall(700, () => {
      this.cameras.main.fadeOut(200, 5, 7, 11);
      this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.start(SCENES.VERSUS, { round }));
    });
  }

  private back(): void {
    versus.leave();
    sfx('ui_back');
    this.scene.start(SCENES.MENU);
  }

  update(_t: number, delta: number): void {
    this.nav?.update();
    const s = versus.status;
    if (s === 'connecting' || s === 'queued') {
      this.dotT += delta / 1000;
      if (this.dotT > 0.4) {
        this.dotT = 0;
        this.dots = (this.dots + 1) % 4;
        this.status.setText((s === 'queued' ? 'SEARCHING' : 'CONNECTING') + '.'.repeat(this.dots));
      }
    }
  }
}
